const EMPTY_ARRAY = Object.freeze([]);

export class WorkspaceObjectRegistry {
  constructor({ schedule = defaultSchedule } = {}) {
    this.providers = new Map();
    this.listeners = new Set();
    this.providerDisposers = new Map();
    this.schedule = schedule;
    this.connected = false;
    this.pendingNotification = false;
    this.revision = 0;
  }

  registerProvider(provider) {
    if (
      !provider
      || typeof provider.id !== "string"
      || !provider.id.trim()
      || typeof provider.getRootNodes !== "function"
    ) {
      throw new TypeError("Object Tree providers require a stable id and getRootNodes().");
    }
    if (this.providers.has(provider.id)) {
      throw new Error(`Object Tree provider "${provider.id}" is already registered.`);
    }
    this.providers.set(provider.id, provider);
    if (this.connected) this.connectProvider(provider);
    this.notify();
    return () => this.unregisterProvider(provider.id);
  }

  unregisterProvider(providerId) {
    const provider = this.providers.get(providerId);
    if (!provider) return false;
    this.providerDisposers.get(providerId)?.();
    this.providerDisposers.delete(providerId);
    this.providers.delete(providerId);
    this.notify();
    return true;
  }

  connect() {
    if (this.connected) return;
    this.connected = true;
    for (const provider of this.providers.values()) this.connectProvider(provider);
    this.notify();
  }

  disconnect() {
    if (!this.connected) return;
    this.connected = false;
    for (const dispose of this.providerDisposers.values()) dispose();
    this.providerDisposers.clear();
  }

  subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Object Tree subscribers must be functions.");
    }
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify() {
    if (this.pendingNotification) return;
    this.pendingNotification = true;
    this.schedule(() => {
      this.pendingNotification = false;
      this.revision += 1;
      for (const listener of [...this.listeners]) listener(this.revision);
    });
  }

  snapshot({ query = "" } = {}) {
    const roots = [];
    const ids = new Set();
    for (const provider of this.providers.values()) {
      let providerRoots = EMPTY_ARRAY;
      try {
        providerRoots = provider.getRootNodes() || EMPTY_ARRAY;
      } catch {
        continue;
      }
      for (const root of providerRoots) {
        const normalized = normalizeNode(root, provider.id, ids);
        if (normalized) roots.push(normalized);
      }
    }
    roots.sort(compareRootNodes);
    const normalizedQuery = String(query).trim().toLocaleLowerCase();
    return normalizedQuery
      ? roots.map((node) => filterNode(node, normalizedQuery)).filter(Boolean)
      : roots;
  }

  resolve(nodeId) {
    return findNode(this.snapshot(), nodeId);
  }

  connectProvider(provider) {
    if (typeof provider.subscribe !== "function") return;
    const dispose = provider.subscribe(() => this.notify());
    if (typeof dispose === "function") this.providerDisposers.set(provider.id, dispose);
  }
}

export function flattenVisibleObjectTree(nodes, expandedIds, { searchActive = false } = {}) {
  const rows = [];
  const visit = (node, level, parentId) => {
    rows.push({ node, level, parentId });
    const expanded = searchActive || expandedIds.has(node.id);
    if (!expanded) return;
    for (const child of node.children) visit(child, level + 1, node.id);
  };
  for (const node of nodes || EMPTY_ARRAY) visit(node, 1, null);
  return rows;
}

export function findNode(nodes, nodeId) {
  for (const node of nodes || EMPTY_ARRAY) {
    if (node.id === nodeId) return node;
    const nested = findNode(node.children, nodeId);
    if (nested) return nested;
  }
  return null;
}

function normalizeNode(node, providerId, ids) {
  if (!node || typeof node !== "object") return null;
  const localId = String(node.id || "").trim();
  const label = String(node.label || "").trim();
  if (!localId || !label) return null;
  const id = localId.includes(":") ? localId : `${providerId}:${localId}`;
  if (ids.has(id)) return null;
  ids.add(id);
  const children = [];
  for (const child of Array.isArray(node.children) ? node.children : EMPTY_ARRAY) {
    const normalized = normalizeNode(child, providerId, ids);
    if (normalized) children.push(normalized);
  }
  return Object.freeze({
    id,
    kind: String(node.kind || "object"),
    label,
    description: String(node.description || ""),
    status: String(node.status || ""),
    stackOrder: finiteNumber(node.stackOrder, 0),
    active: node.active === true,
    selected: node.selected === true,
    hidden: node.hidden === true,
    locked: node.locked === true,
    paused: node.paused === true,
    canActivate: node.canActivate !== false && typeof node.activate === "function",
    canSettings: typeof node.settings === "function",
    canDelete: typeof node.delete === "function",
    settingsDisabledReason: String(node.settingsDisabledReason || ""),
    deleteDisabledReason: String(node.deleteDisabledReason || ""),
    activate: typeof node.activate === "function" ? node.activate : null,
    settings: typeof node.settings === "function" ? node.settings : null,
    delete: typeof node.delete === "function" ? node.delete : null,
    children: Object.freeze(children)
  });
}

function filterNode(node, query) {
  const children = node.children.map((child) => filterNode(child, query)).filter(Boolean);
  const matches = [
    node.label,
    node.description,
    node.kind,
    node.status
  ].some((value) => value.toLocaleLowerCase().includes(query));
  return matches || children.length > 0
    ? Object.freeze({ ...node, children: Object.freeze(children) })
    : null;
}

function compareRootNodes(left, right) {
  return right.stackOrder - left.stackOrder
    || left.label.localeCompare(right.label)
    || left.id.localeCompare(right.id);
}

function finiteNumber(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function defaultSchedule(callback) {
  queueMicrotask(callback);
}
